'use client';

import { JSX, ChangeEvent } from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
import { FormControlLabel, FormHelperText, Switch } from '@mui/material';

import { CreateBetGameSettingType, ResReadBetGameSettingListType } from '@/types';
import { isCheckInteger, packBigNumbers } from '@/lib/BigNumbers';
import Header from '../Header';
import SmallWinSettingForm from './SmallWinSettingForm';

export default function BetGameSettingForm({
  types,
  settings,
  setSettings,
}: {
  types: 'create' | 'update';
  settings: CreateBetGameSettingType | ResReadBetGameSettingListType;
  setSettings: (value: any) => void;
}): JSX.Element {
  const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!name) {
      return;
    }
    if (
      [
        'minTotAmount',
        'betLimitMax',
        'betLimitMin',
        'jackpotAmountRatio',
        'jackpotReadyRatio',
        'burnAmountRatio',
        'winMultiple',
        'type',
        'inviteBetRatio',
        'inviteWinRatio',
        'referralRatio',
      ].includes(name)
    ) {
      const isCheck = isCheckInteger(value);
      if (!isCheck) {
        toast.error('소수점을 사용할 수 없습니다.');
        return;
      }
    }
    setSettings((prev: CreateBetGameSettingType | ResReadBetGameSettingListType) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeSelect = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (!name || !value) {
      return;
    }
    setSettings((prev: CreateBetGameSettingType | ResReadBetGameSettingListType) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setSettings((prev: CreateBetGameSettingType | ResReadBetGameSettingListType) => ({
      ...prev,
      [name]: Number(checked),
    }));
  };

  const title = types === 'create' ? '베팅 게임 설정 생성' : '베팅 게임 설정 수정';

  return (
    <>
      <Card>
        <Header title={title} />
        <Divider />
        <CardContent
          sx={{
            maxHeight: '70vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
          <Grid container spacing={3}>
            <Grid xs={12}>
              <Typography variant="h4">게임 기본 설정</Typography>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>게임 이름</InputLabel>
                <OutlinedInput value={settings.name} label="게임 이름" name="name" onChange={handleChangeValue} />
              </FormControl>
            </Grid>

            {'type' in settings && (
              <Grid md={6} xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>게임 타입</InputLabel>
                  <OutlinedInput
                    value={settings.type}
                    inputProps={{
                      type: 'number',
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    placeholder="게임 타입"
                    label="게임 타입"
                    name="type"
                    onChange={handleChangeValue}
                  />
                </FormControl>
                <FormHelperText>※ 무조건 숫자만</FormHelperText>
              </Grid>
            )}

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>기준 베팅액</InputLabel>
                <OutlinedInput
                  value={settings.minTotAmount}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="기준 베팅액"
                  label="기준 베팅액"
                  name="minTotAmount"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            {'totAmount' in settings && (
              <Grid md={6} xs={12}>
                <FormControl fullWidth required disabled>
                  <InputLabel>현재 베팅 금액</InputLabel>
                  <OutlinedInput
                    value={settings.totAmount}
                    inputProps={{
                      type: 'number',
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    placeholder="현재 베팅 금액"
                    label="현재 베팅 금액"
                    name="totAmount"
                    onChange={handleChangeValue}
                  />
                </FormControl>
                <FormHelperText>※ 자동 증가</FormHelperText>
              </Grid>
            )}

            <Grid xs={12}>
              <Typography variant="h4">베팅 제한 설정</Typography>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>베팅 상한선</InputLabel>
                <OutlinedInput
                  value={settings.betLimitMax}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="베팅 상한선"
                  label="베팅 상한선"
                  name="betLimitMax"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>베팅 하한선</InputLabel>
                <OutlinedInput
                  value={settings.betLimitMin}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="베팅 하한선"
                  label="베팅 하한선"
                  name="betLimitMin"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <Typography variant="h4">잭팟</Typography>
            </Grid>

            {'jackpotAmount' in settings && (
              <Grid md={6} xs={12}>
                <FormControl fullWidth required disabled>
                  <InputLabel>누적 잭팟 금액</InputLabel>
                  <OutlinedInput
                    value={packBigNumbers(settings.jackpotAmount)}
                    inputProps={{
                      type: 'text',
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    placeholder="누적 잭팟 금액"
                    label="누적 잭팟 금액"
                    name="jackpotAmount"
                    onChange={handleChangeValue}
                  />
                </FormControl>
                <FormHelperText>※ 자동 증가</FormHelperText>
              </Grid>
            )}

            {'jackpotReady' in settings && (
              <Grid md={6} xs={12}>
                <FormControl fullWidth required disabled>
                  <InputLabel>누적 잭팟 준비 금액</InputLabel>
                  <OutlinedInput
                    value={packBigNumbers(settings.jackpotReady)}
                    inputProps={{
                      type: 'text',
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    placeholder="누적 잭팟 준비 금액"
                    label="누적 잭팟 준비 금액"
                    name="jackpotReady"
                    onChange={handleChangeValue}
                  />
                </FormControl>
                <FormHelperText>※ 자동 증가</FormHelperText>
              </Grid>
            )}

            <Grid xs={12}>
              <Typography variant="h4">잭팟 비율 설정</Typography>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>잭팟 지급금 비율 (%)</InputLabel>
                <OutlinedInput
                  value={settings.jackpotAmountRatio}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="잭팟 지급금 비율"
                  label="잭팟 지급금 비율"
                  name="jackpotAmountRatio"
                  onChange={handleChangeValue}
                />
                <FormHelperText>총 누적베팅액 대비 비율</FormHelperText>
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>잭팟 준비금 비율 (%)</InputLabel>
                <OutlinedInput
                  value={settings.jackpotReadyRatio}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="잭팟 준비금 비율"
                  label="잭팟 준비금 비율"
                  name="jackpotReadyRatio"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <Typography variant="h4">레퍼럴 서비스 지급 비율 설정</Typography>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>피추천인 베팅금액 대비 리워드 (%)</InputLabel>
                <OutlinedInput
                  value={settings.inviteBetRatio}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="피추천인 베팅금액 대비 리워드"
                  label="피추천인 베팅금액 대비 리워드 (%)"
                  name="inviteBetRatio"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>피추천인 당첨금액 대비 리워드(잭팟 제외) (%)</InputLabel>
                <OutlinedInput
                  value={settings.inviteWinRatio}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="피추천인 당첨금액 대비 리워드(잭팟 제외)"
                  label="피추천인 당첨금액 대비 리워드(잭팟 제외) (%)"
                  name="inviteWinRatio"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>레퍼럴 서비스 운용 비율 (%)</InputLabel>
                <OutlinedInput
                  value={settings.referralRatio}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="레퍼럴 서비스 운용 비율"
                  label="레퍼럴 서비스 운용 비율 (%)"
                  name="referralRatio"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <Typography variant="h4">기타 비율 설정</Typography>
            </Grid>

            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>소각 금액 비율 (%)</InputLabel>
                <OutlinedInput
                  value={settings.burnAmountRatio}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="소각 금액 비율"
                  label="소각 금액 비율"
                  name="burnAmountRatio"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            <Grid xs={12}>
              <Typography variant="h4">당첨 설정</Typography>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>당첨 배수</InputLabel>
                <OutlinedInput
                  value={settings.winMultiple}
                  inputProps={{
                    type: 'number',
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  placeholder="당첨 배수"
                  label="당첨 배수"
                  name="winMultiple"
                  onChange={handleChangeValue}
                />
              </FormControl>
            </Grid>

            {types === 'update' && 'gameSettingId' in settings && (
              <Grid xs={12}>
                <SmallWinSettingForm gameSettingId={settings.gameSettingId} />
              </Grid>
            )}

            {types === 'update' && 'isActive' in settings && (
              <Grid md={6} xs={12}>
                <FormControl fullWidth required>
                  <FormControlLabel
                    control={<Switch checked={Boolean(settings.isActive)} onChange={handleSwitch} name="isActive" />}
                    label="게임 활성화 상태"
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          {/* <Button type="submit" variant="contained">
            {types === 'create' ? 'Create' : 'Update'}
          </Button> */}
        </CardActions>
      </Card>
    </>
  );
}
